import React from 'react';
import {
    Alert,
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Typography,
} from '@mui/material';
import { Flow } from '@lendica/components';

// ------------------------------
// Minimal, reusable server-side MUI table
// ------------------------------

export type PaginatedResponse<T> = {
    count: number;
    next: string | null;
    previous: string | null;
    results: T[];
};

export type Fetcher<T> = (args: {
    page: number; // 1-indexed
    pageSize: number;
    signal?: AbortSignal;
}) => Promise<PaginatedResponse<T>>;

export type CellContentProps<V, R> = {
    value: V;
    row: R;
    rowIndex: number;
};

// Column API compatible with your existing StaticTable columns
export type Column<R> = {
    id: keyof R & string;
    label: React.ReactNode;
    formatter?: (value: any, row?: R) => React.ReactNode;
    CellContent?: (props: CellContentProps<any, R>) => React.ReactNode;
    align?: 'left' | 'center' | 'right';
    tableCellProps?: React.ComponentProps<typeof TableCell>;
};

export type MuiPaginatedTableProps<R> = {
    fetcher: Fetcher<R>;
    columns: Column<R>[];
    getRowKey: (row: R, index: number) => React.Key;
    toolbarTitle?: React.ReactNode;

    // pagination
    initialPage?: number; // default 1
    initialPageSize?: number; // default 20
    pageSizeOptions?: number[]; // default [10,20,50,100]
    syncUrlParams?: boolean; // default true (uses ?page=&page_size=)

    // controlled (optional)
    page?: number;
    pageSize?: number;
    onPageChange?: (page: number) => void;
    onPageSizeChange?: (size: number) => void;

    // UX
    renderLoading?: React.ReactNode;
    renderEmpty?: React.ReactNode;
    renderError?: (message: string) => React.ReactNode;
    onRowClick?: (row: R, rowIndex: number) => void;
};

function getIntParam(search: URLSearchParams, key: string, fallback: number) {
    const raw = search.get(key);
    const n = raw ? parseInt(raw, 10) : NaN;
    return Number.isFinite(n) && n > 0 ? n : fallback;
}

function useQueryParamsSync(enabled: boolean, page: number, pageSize: number) {
    React.useEffect(() => {
        if (!enabled) return;
        const url = new URL(window.location.href);
        url.searchParams.set('page', String(page));
        url.searchParams.set('page_size', String(pageSize));
        window.history.replaceState({}, '', url);
    }, [enabled, page, pageSize]);
}

export function PaginatedMuiTable<R>(props: MuiPaginatedTableProps<R>) {
    const {
        fetcher,
        columns,
        getRowKey,
        initialPage = 1,
        initialPageSize = 20,
        pageSizeOptions = [10, 20, 50, 100],
        syncUrlParams = true,
        page: controlledPage,
        pageSize: controlledPageSize,
        onPageChange,
        onPageSizeChange,
        renderLoading,
        renderEmpty,
        renderError,
        onRowClick,
    } = props;

    // initialize from URL if desired
    const initialFromUrl = React.useMemo(() => {
        if (!syncUrlParams) return { p: initialPage, ps: initialPageSize };
        const sp = new URLSearchParams(window.location.search);
        const p = getIntParam(sp, 'page', initialPage);
        const psRaw = getIntParam(sp, 'page_size', initialPageSize);
        const ps = pageSizeOptions.includes(psRaw) ? psRaw : initialPageSize;
        return { p, ps };
    }, [initialPage, initialPageSize, pageSizeOptions, syncUrlParams]);

    const [uPage, setUPage] = React.useState(initialFromUrl.p);
    const [uPageSize, setUPageSize] = React.useState(initialFromUrl.ps);
    const page = controlledPage ?? uPage; // 1-indexed for API
    const pageSize = controlledPageSize ?? uPageSize;

    const setPage = (p: number) => {
        if (controlledPage != null) onPageChange?.(p);
        else setUPage(p);
    };
    const setPageSize = (ps: number) => {
        if (controlledPageSize != null) onPageSizeChange?.(ps);
        else setUPageSize(ps);
    };

    useQueryParamsSync(syncUrlParams, page, pageSize);

    // data
    const [data, setData] = React.useState<PaginatedResponse<R> | null>(null);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState<string | null>(null);

    React.useEffect(() => {
        const abort = new AbortController();
        setLoading(true);
        setError(null);
        fetcher({ page, pageSize, signal: abort.signal })
            .then(d => setData(d))
            .catch(e => {
                if (e?.name !== 'AbortError') setError(e?.message || 'Unknown error');
            })
            .finally(() => setLoading(false));
        return () => abort.abort();
    }, [fetcher, page, pageSize]);

    const total = data?.count ?? 0;
    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    React.useEffect(() => {
        if (page > totalPages) setPage(totalPages);
        if (page < 1) setPage(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [totalPages]);

    const muiPage = page - 1; // MUI TablePagination is 0-indexed
    const handleMuiPageChange = (_: unknown, newMuiPage: number) => setPage(newMuiPage + 1);
    const handleRowsPerPageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const nextSize = parseInt(e.target.value, 10);
        setPageSize(nextSize);
        setPage(1);
    };

    return (
        <Paper elevation={0} sx={{ width: '100%' }}>
            <TableContainer component={Paper} elevation={0}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            {columns.map(c => (
                                <TableCell key={c.id} align={c.align} {...c.tableCellProps}>
                                    {c.label}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {error ? (
                            <TableRow sx={{ width: '100%' }}>
                                <TableCell colSpan={columns.length} sx={{ width: '100%', px: 0 }}>
                                    {renderError ? (
                                        renderError(error)
                                    ) : (
                                        <Alert severity="info" sx={{ width: '100%' }}>
                                            {error}
                                        </Alert>
                                    )}
                                </TableCell>
                            </TableRow>
                        ) : loading ? (
                            <TableRow>
                                <TableCell colSpan={columns.length}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            gap: 1,
                                            p: 2,
                                            width: '100%',
                                        }}
                                    >
                                        <Flow />
                                        <Typography variant="body2" color="inherit" align="center">
                                            {renderLoading ?? 'Loading…'}
                                        </Typography>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ) : data?.results?.length ? (
                            data.results.map((row, rowIndex) => (
                                <TableRow
                                    key={getRowKey(row, rowIndex)}
                                    onClick={
                                        onRowClick ? () => onRowClick(row, rowIndex) : undefined
                                    }
                                    hover={!!onRowClick}
                                    style={{ cursor: onRowClick ? 'pointer' : undefined }}
                                >
                                    {columns.map((c, colIndex) => {
                                        const raw = (row as any)[c.id];
                                        const content = c.CellContent
                                            ? c.CellContent({ value: raw, row, rowIndex })
                                            : c.formatter
                                            ? c.formatter(raw, row)
                                            : raw;
                                        return (
                                            <TableCell
                                                key={c.id ?? colIndex}
                                                align={c.align}
                                                {...c.tableCellProps}
                                            >
                                                {content}
                                            </TableCell>
                                        );
                                    })}
                                </TableRow>
                            ))
                        ) : (
                            <TableRow sx={{ width: '100%' }}>
                                <TableCell colSpan={columns.length} sx={{ width: '100%', px: 0 }}>
                                    {renderEmpty ?? (
                                        <Alert severity="info" sx={{ width: '100%' }}>
                                            No deals found.
                                        </Alert>
                                    )}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <TablePagination
                component="div"
                count={total}
                page={muiPage}
                onPageChange={handleMuiPageChange}
                rowsPerPage={pageSize}
                onRowsPerPageChange={handleRowsPerPageChange}
                rowsPerPageOptions={pageSizeOptions}
                labelRowsPerPage="Rows per page:"
            />
        </Paper>
    );
}
